import { useState } from 'react';
import { Loader2, Send, CheckCircle2, X, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { submitReview } from '../lib/api';

const initial = { name: '', rating: 5, location: '', text: '' };

export const ReviewDialog = ({ open, onOpenChange, onSubmitted }) => {
    const [form, setForm] = useState(initial);
    const [hoverRating, setHoverRating] = useState(0);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleClose = (next) => {
        onOpenChange(next);
        if (!next) {
            setTimeout(() => { setForm(initial); setDone(false); setHoverRating(0); }, 250);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || form.text.length < 10) {
            toast.error('Please add your name and a short review (min 10 chars).');
            return;
        }
        setLoading(true);
        try {
            const created = await submitReview(form);
            setDone(true);
            toast.success('Thank you — your review is live.');
            onSubmitted?.(created);
        } catch {
            toast.error('Could not submit your review. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const activeRating = hoverRating || form.rating;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="review-form"
                className="sm:max-w-[520px] p-0 overflow-hidden border-border rounded-2xl"
            >
                <button
                    onClick={() => handleClose(false)}
                    className="absolute right-4 top-4 z-10 grid place-items-center w-8 h-8 rounded-full bg-white/80 backdrop-blur hover:bg-white border border-border"
                    aria-label="Close"
                    data-testid="review-form-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {done ? (
                    <div className="p-10 text-center flex flex-col items-center gap-4" data-testid="review-success">
                        <span className="grid place-items-center w-14 h-14 rounded-full bg-accent/30 text-primary">
                            <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
                        </span>
                        <DialogHeader className="text-center">
                            <DialogTitle className="font-outfit text-2xl">Review posted</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                Thank you for taking the time — your review is now live on our site.
                            </DialogDescription>
                        </DialogHeader>
                        <Button onClick={() => handleClose(false)} className="rounded-full bg-primary text-primary-foreground mt-2">Close</Button>
                    </div>
                ) : (
                    <div className="p-7 md:p-9">
                        <DialogHeader>
                            <span className="overline">Leave a review</span>
                            <DialogTitle className="font-outfit text-2xl mt-2">How was your clean?</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                Honest feedback helps us, and helps your neighbours pick a cleaner with confidence.
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">Rating</Label>
                                <div className="flex items-center gap-1.5" data-testid="review-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onMouseEnter={() => setHoverRating(n)}
                                            onMouseLeave={() => setHoverRating(0)}
                                            onClick={() => setForm((f) => ({ ...f, rating: n }))}
                                            data-testid={`review-rating-${n}`}
                                            aria-label={`${n} star${n > 1 ? 's' : ''}`}
                                            className="p-1 transition-transform hover:scale-110"
                                        >
                                            <Star
                                                className={`w-7 h-7 transition-colors ${
                                                    n <= activeRating
                                                        ? 'fill-amber-400 text-amber-400'
                                                        : 'text-muted-foreground/40'
                                                }`}
                                                strokeWidth={1.4}
                                            />
                                        </button>
                                    ))}
                                    <span className="ml-3 text-sm text-muted-foreground font-outfit">{activeRating} / 5</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="r-name" className="text-xs text-muted-foreground">Your name</Label>
                                    <Input
                                        id="r-name"
                                        data-testid="review-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Eleanor R."
                                        required
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="r-loc" className="text-xs text-muted-foreground">Area (optional)</Label>
                                    <Input
                                        id="r-loc"
                                        data-testid="review-input-location"
                                        value={form.location}
                                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                                        placeholder="Clifton, BS8"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="r-text" className="text-xs text-muted-foreground">Your review</Label>
                                <Textarea
                                    id="r-text"
                                    data-testid="review-input-text"
                                    value={form.text}
                                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                                    rows={4}
                                    minLength={10}
                                    maxLength={600}
                                    placeholder="Tell other clients what you thought of the service…"
                                    required
                                />
                                <div className="text-[11px] text-muted-foreground text-right">
                                    {form.text.length} / 600
                                </div>
                            </div>

                            <Button
                                type="submit"
                                data-testid="review-form-submit"
                                disabled={loading}
                                className="w-full rounded-full bg-primary text-primary-foreground font-outfit h-12 text-sm hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(47,130,105,0.55)] transition-all"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Post review</>}
                            </Button>
                        </form>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
